"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
	Loader2,
	KeySquare,
	Github,
	Terminal,
	Lock,
	Activity,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useAuth } from "@/lib/auth-client";
import { Button } from "@/lib/components/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/lib/components/form";
import { Input } from "@/lib/components/input";
import { toast } from "@/lib/components/use-toast";

const formSchema = z.object({
	email: z.string().email({
		message: "Please enter a valid email address",
	}),
	password: z.string().min(8, {
		message: "Password must be at least 8 characters",
	}),
});

export default function Login() {
	const queryClient = useQueryClient();
	const _router = useRouter();
	const posthog = usePostHog();
	const [isLoading, setIsLoading] = useState(false);
	const [isPageLoaded, setIsPageLoaded] = useState(false);
	const [isRedirecting, setIsRedirecting] = useState(false);
	const { signIn } = useAuth();

	// Don't use useUser redirect here - we handle redirect explicitly after login
	// useUser({
	// 	redirectTo: "/dashboard",
	// 	redirectWhen: "authenticated",
	// 	checkOnboarding: true,
	// });

	useEffect(() => {
		setIsPageLoaded(true);
		posthog.capture("page_viewed_login");
	}, [posthog]);

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
			password: "",
		},
	});

	useEffect(() => {
		if (window.PublicKeyCredential) {
			void signIn.passkey({ autoFill: true });
		}
	}, [signIn]);

	async function onSubmit(values: z.infer<typeof formSchema>) {
		setIsLoading(true);
		const { error } = await signIn.email(
			{
				email: values.email,
				password: values.password,
			},
			{
				onSuccess: (ctx) => {
					queryClient.clear();
					posthog.identify(ctx.data.user.id, {
						email: ctx.data.user.email,
						name: ctx.data.user.name,
					});
					posthog.capture("user_logged_in", {
						method: "email",
						email: values.email,
					});
					toast({ title: "Login successful" });
					setIsRedirecting(true);
					// Use window.location for hard redirect to ensure fresh page load
					window.location.href = "/dashboard";
				},
				onError: (ctx) => {
					toast({
						title: ctx.error.message || "An unknown error occurred",
						variant: "destructive",
					});
				},
			},
		);

		if (error) {
			toast({
				title: error.message || "An unknown error occurred",
				variant: "destructive",
			});
		}

		setIsLoading(false);
	}

	async function handlePasskeySignIn() {
		setIsLoading(true);
		try {
			const res = await signIn.passkey();
			if (res?.error) {
				toast({
					title: res.error.message || "Failed to sign in with passkey",
					variant: "destructive",
				});
				return;
			}
			posthog.capture("user_logged_in", { method: "passkey" });
			toast({ title: "Login successful" });
			setIsRedirecting(true);
			// Use window.location for hard redirect to ensure fresh page load
			window.location.href = "/dashboard";
		} catch (error: unknown) {
			toast({
				title: (error as Error)?.message || "Failed to sign in with passkey",
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<div
			className={`min-h-screen bg-background text-muted-foreground font-mono selection:bg-primary/30 selection:text-primary-foreground transition-opacity duration-500 ${isPageLoaded ? "opacity-100" : "opacity-0"}`}
		>
			{/* Redirecting overlay */}
			{isRedirecting && (
				<div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center">
					<div className="flex flex-col items-center space-y-4">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
						<p className="text-sm text-muted-foreground">
							Redirecting to dashboard...
						</p>
					</div>
				</div>
			)}

			{/* Technical grid background */}
			<div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiMxMTEiIGQ9Ik0wIDBoNDB2NDBoLTQweiIvPjxwYXRoIHN0cm9rZT0iIzMzMyIgZD0iTTAgMjBoNDBtLTIwLTIwdjQwbS0xNS0zNXY1bS01IDV2NW01IDV2NW01IDV2NW0xMC0yNXY1bTUgNXY1bS01IDV2NW0tNSA1djUiLz48Y2lyY2xlIGN4PSI1IiBjeT0iNSIgcj0iLjUiIGZpbGw9IiM1NTUiLz48Y2lyY2xlIGN4PSIxMCIgY3k9IjEwIiByPSIuNSIgZmlsbD0iIzU1NSIvPjxjaXJjbGUgY3g9IjE1IiBjeT0iMTUiIHI9Ii41IiBmaWxsPSIjNTU1Ii8+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iLjUiIGZpbGw9IiM1NTUiLz48Y2lyY2xlIGN4PSIyNSIgY3k9IjI1IiByPSIuNSIgZmlsbD0iIzU1NSIvPjxjaXJjbGUgY3g9IjMwIiBjeT0iMzAiIHI9Ii41IiBmaWxsPSIjNTU1Ii8+PGNpcmNsZSBjeD0iMzUiIGN5PSIzNSIgcj0iLjUiIGZpbGw9IiM1NTUiLz48Y2lyY2xlIGN4PSI1IiBjeT0iMTUiIHI9Ii41IiBmaWxsPSIjNTU1Ii8+PGNpcmNsZSBjeD0iMTAiIGN5PSIyMCIgcj0iLjUiIGZpbGw9IiM1NTUiLz48Y2lyY2xlIGN4PSIxNSIgY3k9IjI1IiByPSIuNSIgZmlsbD0iIzU1NSIvPjxjaXJjbGUgY3g9IjIwIiBjeT0iMzAiIHI9Ii41IiBmaWxsPSIjNTU1Ii8+PGNpcmNsZSBjeD0iMjUiIGN5PSIzNSIgcj0iLjUiIGZpbGw9IiM1NTUiLz48Y2lyY2xlIGN4PSIxNSIgY3k9IjUiIHI9Ii41IiBmaWxsPSIjNTU1Ii8+PGNpcmNsZSBjeD0iMjAiIGN5PSIxMCIgcj0iLjUiIGZpbGw9IiM1NTUiLz48Y2lyY2xlIGN4PSIyNSIgY3k9IjE1IiByPSIuNSIgZmlsbD0iIzU1NSIvPjxjaXJjbGUgY3g9IjMwIiBjeT0iMjAiIHI9Ii41IiBmaWxsPSIjNTU1Ii8+PGNpcmNsZSBjeD0iMzUiIGN5PSIyNSIgcj0iLjUiIGZpbGw9IiM1NTUiLz48L2c+PC9zdmc+')]" />
			<div className="fixed inset-0 bg-gradient-to-b from-background via-background/95 to-background pointer-events-none" />

			{/* Header */}
			<header className="relative z-50 border-b border-border backdrop-blur-sm bg-background/80">
				<div className="container mx-auto px-6">
					<div className="flex justify-between items-center h-16">
						<Link href="/" className="flex items-center space-x-3 group">
							<div className="relative">
								<div className="w-9 h-9 bg-card border border-primary/50 flex items-center justify-center transition-colors group-hover:border-primary">
									<div className="w-4 h-4 bg-primary/80" />
								</div>
							</div>
							<div className="flex flex-col leading-none">
								<span className="text-base font-bold text-foreground tracking-tight">
									VISCA
								</span>
								<span className="text-[10px] text-primary tracking-widest">
									AI SYSTEMS
								</span>
							</div>
						</Link>

						<div className="flex items-center space-x-6">
							<div className="hidden md:flex items-center space-x-2 text-xs">
								<Activity className="w-3 h-3 text-primary" />
								<span className="text-muted-foreground">SYSTEMS:</span>
								<span className="text-primary">OPERATIONAL</span>
							</div>
							<Link
								href="/signup"
								className="text-xs text-muted-foreground hover:text-primary transition-colors uppercase tracking-wider"
							>
								Create Account
							</Link>
						</div>
					</div>
				</div>
			</header>

			{/* Main Content */}
			<div className="relative z-10 flex min-h-[calc(100vh-64px)] items-center justify-center px-4 py-12">
				<div className="w-full max-w-[480px]">
					{/* System Status Banner */}
					<div className="mb-8 flex items-center justify-center">
						<div className="inline-flex items-center text-xs bg-primary/10 border border-border px-4 py-2">
							<Lock className="w-3 h-3 mr-2 text-primary" />
							<span className="text-muted-foreground uppercase tracking-widest">
								Secure Authentication Portal
							</span>
						</div>
					</div>

					{/* Title Section */}
					<div className="text-center mb-10">
						<h1 className="text-3xl font-bold text-foreground mb-3 tracking-tight uppercase">
							Access Control
						</h1>
						<p className="text-sm text-muted-foreground uppercase tracking-wide">
							Enter credentials to authenticate
						</p>
					</div>

					{/* Main Terminal-Style Card */}
					<div className="relative">
						{/* Glow effect */}
						<div className="absolute -inset-0.5 bg-primary/20 blur-sm" />

						{/* Main card */}
						<div className="relative bg-card border border-border">
							{/* Terminal header bar */}
							<div className="flex items-center justify-between px-4 py-2 border-b border-border bg-primary/10">
								<div className="flex items-center space-x-2">
									<Terminal className="w-3 h-3 text-primary" />
									<span className="text-xs text-primary uppercase tracking-wider">
										auth_session.init
									</span>
								</div>
								<div className="flex space-x-1.5">
									<div className="w-2 h-2 rounded-full bg-red-500/70" />
									<div className="w-2 h-2 rounded-full bg-yellow-500/70" />
									<div className="w-2 h-2 rounded-full bg-primary/70" />
								</div>
							</div>

							{/* Form content */}
							<div className="p-8">
								<Form {...form}>
									<form
										onSubmit={form.handleSubmit(onSubmit)}
										className="space-y-6"
									>
										<FormField
											control={form.control}
											name="email"
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-muted-foreground text-xs uppercase tracking-widest mb-2 block">
														<span className="text-primary mr-2">$</span>
														Email Address
													</FormLabel>
													<FormControl>
														<Input
															placeholder="user@domain.com"
															type="email"
															autoComplete="username webauthn"
															className="h-11 bg-card/50 border-border text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:ring-1 focus:ring-primary/50 font-mono text-sm"
															{...field}
														/>
													</FormControl>
													<FormMessage className="text-red-400 text-xs mt-1.5 font-mono" />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="password"
											render={({ field }) => (
												<FormItem>
													<div className="flex items-center justify-between mb-2">
														<FormLabel className="text-muted-foreground text-xs uppercase tracking-widest">
															<span className="text-primary mr-2">$</span>
															Password
														</FormLabel>
														{/* Removed link that was causing type errors */}
														<span className="text-xs text-primary/70 uppercase tracking-wider">
															Reset
														</span>
													</div>
													<FormControl>
														<Input
															placeholder="••••••••••••"
															type="password"
															autoComplete="current-password webauthn"
															className="h-11 bg-card/50 border-border text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:ring-1 focus:ring-primary/50 font-mono text-sm"
															{...field}
														/>
													</FormControl>
													<FormMessage className="text-red-400 text-xs mt-1.5 font-mono" />
												</FormItem>
											)}
										/>

										{/* Submit Button */}
										<div className="pt-2">
											<Button
												type="submit"
												disabled={isLoading}
												className="w-full h-12 bg-primary/10 border border-primary/50 text-primary hover:bg-primary/20 hover:border-primary transition-all font-mono uppercase tracking-wider text-sm disabled:opacity-50"
											>
												{isLoading ? (
													<>
														<Loader2 className="mr-2 h-4 w-4 animate-spin" />
														Authenticating...
													</>
												) : (
													<>
														<Terminal className="mr-2 h-4 w-4" />
														Initiate Session
													</>
												)}
											</Button>
										</div>
									</form>
								</Form>

								{/* Divider */}
								<div className="relative my-8">
									<div className="absolute inset-0 flex items-center">
										<div className="w-full border-t border-border" />
									</div>
									<div className="relative flex justify-center">
										<span className="px-3 bg-card text-xs text-muted-foreground uppercase tracking-widest">
											Alt. Auth Methods
										</span>
									</div>
								</div>

								{/* Alternative auth */}
								<div className="space-y-3">
									<Button
										onClick={async () => {
											setIsLoading(true);
											try {
												const res = await signIn.social({
													provider: "github",
													callbackURL:
														location.protocol +
														"//" +
														location.host +
														"/dashboard",
												});
												if (res?.error) {
													toast({
														title:
															res.error.message ||
															"Failed to sign in with GitHub",
														variant: "destructive",
													});
												}
											} finally {
												setIsLoading(false);
											}
										}}
										variant="outline"
										disabled={isLoading}
										className="w-full h-11 bg-card/50 border-border text-muted-foreground hover:bg-card hover:text-primary hover:border-primary/40 transition-all font-mono text-xs uppercase tracking-wider"
									>
										{isLoading ? (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										) : (
											<Github className="mr-2 h-4 w-4" />
										)}
										GitHub Protocol
									</Button>

									<Button
										onClick={handlePasskeySignIn}
										variant="outline"
										disabled={isLoading}
										className="w-full h-11 bg-card/50 border-border text-muted-foreground hover:bg-card hover:text-primary hover:border-primary/40 transition-all font-mono text-xs uppercase tracking-wider"
									>
										{isLoading ? (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										) : (
											<KeySquare className="mr-2 h-4 w-4" />
										)}
										Passkey Authentication
									</Button>
								</div>

								{/* Footer link */}
								<div className="mt-8 pt-6 border-t border-border text-center">
									<p className="text-xs text-muted-foreground uppercase tracking-wider">
										No credentials?{" "}
										<Link
											href="/signup"
											className="text-primary hover:text-primary/80 transition-colors"
										>
											Request Access
										</Link>
									</p>
								</div>
							</div>
						</div>
					</div>

					{/* Security indicators */}
					<div className="mt-8 flex items-center justify-center gap-8 text-xs">
						<div className="flex items-center gap-2">
							<div className="w-1 h-1 bg-primary rounded-full animate-pulse" />
							<span className="text-muted-foreground uppercase tracking-wider">
								TLS 1.3
							</span>
						</div>
						<div className="flex items-center gap-2">
							<div className="w-1 h-1 bg-primary rounded-full animate-pulse" />
							<span className="text-muted-foreground uppercase tracking-wider">
								256-bit AES
							</span>
						</div>
						<div className="flex items-center gap-2">
							<div className="w-1 h-1 bg-primary rounded-full animate-pulse" />
							<span className="text-muted-foreground uppercase tracking-wider">
								SOC 2 Type II
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Footer */}
			<footer className="relative z-10 border-t border-border">
				<div className="container mx-auto px-6 py-4">
					<div className="flex flex-col sm:flex-row items-center justify-between text-xs text-muted-foreground space-y-2 sm:space-y-0">
						<div className="flex items-center gap-4 uppercase tracking-wider">
							<span>© 2024 VISCA</span>
							<span className="text-muted">|</span>
							<Link
								href="/privacy"
								className="hover:text-foreground transition-colors"
							>
								Privacy
							</Link>
							<span className="text-muted">|</span>
							<Link
								href="/terms"
								className="hover:text-foreground transition-colors"
							>
								Terms
							</Link>
						</div>
						<div className="flex items-center gap-2 uppercase tracking-wider">
							<span className="text-muted-foreground">Ver</span>
							<span className="text-primary/50">3.2.1</span>
						</div>
					</div>
				</div>
			</footer>
		</div>
	);
}
